import type {
  IApplicationGroup,
  IGroupMappingItem,
  IIdpGroup,
  IUiGroupMapping,
} from "@models";
import { mappingRowId } from "@models";

export type MappingFilterTab = "all" | "mapped" | "unmapped";

export type GroupKanbanKind = "app" | "idp";

export interface IGroupKanbanItem {
  id: string;
  name: string;
  description?: string | null;
  kind: GroupKanbanKind;
}

export const KANBAN_COLUMN_APP = "applicationGroups";
export const KANBAN_COLUMN_MAPPING = "mapping";
export const KANBAN_COLUMN_IDP = "idpGroups";

export const MAPPING_ZONE_KANBAN_ITEM: IGroupKanbanItem = {
  id: "__mapping_zone__",
  name: "",
  kind: "app",
};

export function isMappingZoneKanbanItem(item: IGroupKanbanItem): boolean {
  return item.id === MAPPING_ZONE_KANBAN_ITEM.id;
}

/** Figma / Command Center application group mapping palette */
export const MAPPING_FIGMA_COLORS = {
  appBlue: "#1E417A",
  idpGreen: "#087443",
  mappingGray: "#525252",
  idpMappingAccent: "#1C75BC",
  hintText: "rgba(0, 0, 0, 0.55)",
  appMappedBg: "rgba(30, 65, 122, 0.08)",
  appMappedBorder: "rgba(30, 65, 122, 0.35)",
  idpMappedBg: "rgba(8, 116, 67, 0.08)",
  idpMappedBorder: "rgba(8, 116, 67, 0.35)",
} as const;

export function toUiMappings(
  items: IGroupMappingItem[],
  appGroups: IApplicationGroup[],
  idpGroups: IIdpGroup[],
): IUiGroupMapping[] {
  const appById = new Map(appGroups.map((group) => [group.id, group]));
  const idpById = new Map(idpGroups.map((group) => [group.id, group]));

  return items.map((item) => ({
    id: mappingRowId(item.applicationGroupId, item.idpGroupId),
    applicationGroupId: item.applicationGroupId,
    applicationGroupName:
      item.applicationGroupName?.trim() ||
      appById.get(item.applicationGroupId)?.name ||
      item.applicationGroupId,
    idpGroupId: item.idpGroupId,
    idpGroupName: idpById.get(item.idpGroupId)?.name ?? item.idpGroupId,
  }));
}

export function filterMappings(
  mappings: IUiGroupMapping[],
  search: string,
): IUiGroupMapping[] {
  const query = search.trim().toLowerCase();
  if (!query) return mappings;
  return mappings.filter(
    (mapping) =>
      mapping.applicationGroupName.toLowerCase().includes(query) ||
      mapping.idpGroupName.toLowerCase().includes(query),
  );
}

export function filterGroupList<
  T extends { id: string; name: string; description?: string | null },
>(
  groups: T[],
  search: string,
  filter: MappingFilterTab,
  mappedIds: Set<string>,
): T[] {
  const query = search.trim().toLowerCase();
  return groups.filter((group) => {
    const description = (group.description || "").toLowerCase();
    const matchesSearch =
      !query ||
      group.name.toLowerCase().includes(query) ||
      description.includes(query);
    if (!matchesSearch) return false;
    if (filter === "mapped") return mappedIds.has(group.id);
    if (filter === "unmapped") return !mappedIds.has(group.id);
    return true;
  });
}

export function toKanbanItem(
  group: { id: string; name: string; description?: string | null },
  kind: GroupKanbanKind,
): IGroupKanbanItem {
  return {
    id: group.id,
    name: group.name,
    description: group.description,
    kind,
  };
}

export function getGroupKanbanItemId(item: IGroupKanbanItem): string {
  return `${item.kind}:${item.id}`;
}

export function mappingsEqual(
  left: IUiGroupMapping[],
  right: IUiGroupMapping[],
): boolean {
  if (left.length !== right.length) return false;
  const rightKeys = new Set(
    right.map((m) => `${m.applicationGroupId}:${m.idpGroupId}`),
  );
  return left.every((m) =>
    rightKeys.has(`${m.applicationGroupId}:${m.idpGroupId}`),
  );
}
